<script lang="ts" setup>
import { PropType, computed, onMounted, ref } from 'vue';
import { NEW_ID } from '@/util/constants';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { useStore } from 'vuex';
import { ContentStoreState, ContentStoreActions } from '@/stores/content.store';
import { DataResponse } from '@/services/base.service';
import { ScheduleItem } from '@/models/schedule-item.model';
import { Initiative } from '@/models/initiative.model';
import DateField from '@/components/common/DateField.vue';

const events = useEvents();
const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();

const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);

const props = defineProps({
  data: {
    type: Object as PropType<ScheduleItem>,
    required: true,
  },
});

const model = ref<ScheduleItem>({
  ...props.data,
});

const action = computed<string>(() =>
  model.value.id === NEW_ID ? 'Add' : 'Save',
);

const valid = ref<boolean>(true);

const cancel = () => {
  store.commit(Mutations.HideDrawer);
};

const saveItem = async () => {
  let res: DataResponse<ScheduleItem | null> = {
    status: false,
    data: null,
  };

  if (model.value.id === NEW_ID) {
    res = await store.dispatch(
      ContentStoreActions.CreateScheduleItem,
      model.value,
    );
  } else {
    res = await store.dispatch(
      ContentStoreActions.UpdateScheduleItem,
      model.value,
    );
  }

  if (res.status) {
    store.dispatch(Actions.ShowNotification, {
      message: `Saved Schedule`,
      type: 'success',
    });
    events.emit(Event.UpdateContentItem);
    cancel();
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: `Failed to saved Schedule`,
      type: 'error',
    });
  }
};

const removeItem = async () => {
  if (
    model.value &&
    model.value.id !== NEW_ID &&
    confirm(`Are you absolutely sure you want to delete this Schedule?`)
  ) {
    store.dispatch(ContentStoreActions.DeleteScheduleItem, model.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

onMounted(() => {
  store.dispatch(ContentStoreActions.GetInitiatives);
});
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveItem">
    <v-container>
      <h2>{{ $t(action) }} Scheduled Initiative</h2>
      <v-alert type="info">
        A scheduled initiative will be used for <i>initiative of the day</i> on
        the date chosen. Add a description for clarity why this was chosen.
      </v-alert>
      <v-row class="mt-8">
        <v-col>
          <v-text-field
            v-model="model.description"
            label="Description"
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <DateField v-model="model.date" label="Date" required></DateField>
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="model.initiative"
            :items="initiatives"
            label="Initiative"
            item-text="title"
            return-object
            required
          ></v-autocomplete>
        </v-col>
      </v-row>
    </v-container>
    <v-row>
      <v-col col="2"></v-col>
      <v-col>
        <v-btn
          class="mt-2"
          type="button"
          color="warning"
          @click="removeItem"
          block
          v-if="model.id !== -1"
          >Delete</v-btn
        >
      </v-col>
      <v-col>
        <v-btn class="mt-2" type="button" @click="cancel" block>Cancel</v-btn>
      </v-col>
      <v-col>
        <v-btn class="mt-2" type="submit" color="success" block>Edit</v-btn>
      </v-col>
      <v-col col="2"></v-col>
    </v-row>
  </v-form>
</template>
